import type { AppointmentColor } from "../schemas/scheduling.js";

export const VISIT_TYPE_CODES = [
  "fiber_installation",
  "fiber_repair",
  "fiber_splicing",
  "fiber_testing",
  "outside_plant_construction",
  "structured_cabling",
  "wireless_small_cell_das",
  "network_maintenance",
] as const;

export type VisitTypeCode = (typeof VISIT_TYPE_CODES)[number];

export interface VisitTypeSeed {
  /** `visit_types.code`. The stable identifier an import or a log matches on. */
  code: VisitTypeCode;
  name: string;
  description: string;
}

/**
 * The rows `npm run bootstrap` writes into `public.visit_types`.
 *
 * This is the **seed**, not the authority - the same relationship `ROLES` has
 * with `public.roles`. Clients read the table, never this array, which is how
 * Flutter gets the list without a code generator and how the catalogue becomes
 * editable without a deploy. See docs/decisions.md #39.
 *
 * Colour and display order are **not** here and not columns either - they are
 * presentation, and they live in `VISIT_TYPE_PRESENTATION` below.
 */
export const VISIT_TYPE_SEED: readonly VisitTypeSeed[] = [
  {
    code: "fiber_installation",
    name: "Fiber Installation",
    description: "New fibre service run and terminated at a customer premises",
  },
  {
    code: "fiber_repair",
    name: "Fiber Repair",
    description: "Fault response on damaged or degraded fibre",
  },
  {
    code: "fiber_splicing",
    name: "Fiber Splicing",
    description: "Fusion splicing and closure work in the field",
  },
  {
    code: "fiber_testing",
    name: "Fiber Testing",
    description: "OTDR, power meter and certification of an existing run",
  },
  {
    code: "outside_plant_construction",
    name: "Outside Plant Construction",
    description: "Aerial and underground plant build, including pole and duct work",
  },
  {
    code: "structured_cabling",
    name: "Structured Cabling",
    description: "In-building copper and fibre cabling to a customer's rack",
  },
  {
    code: "wireless_small_cell_das",
    name: "Wireless / Small Cell / DAS",
    description: "Small cell, DAS and rooftop wireless installation and service",
  },
  {
    code: "network_maintenance",
    name: "Network Maintenance",
    description: "Scheduled preventive work on live network plant",
  },
] as const;

export function isVisitTypeCode(value: unknown): value is VisitTypeCode {
  return typeof value === "string" && (VISIT_TYPE_CODES as readonly string[]).includes(value);
}

/**
 * How a work type is drawn and ordered. Client-side, keyed off `code`.
 *
 * Not columns on `visit_types`, because neither is data a customer edits and a
 * column is something two clients then have to read for no reason. The phone
 * mirrors this by hand in `mobile-app/lib/core/constants/work.dart` - the same
 * arrangement `SEVERITY_META` already has for its icon, where the Lucide and
 * Material names genuinely differ and one column could never hold both.
 *
 * `order` is spaced by ten so a type can be slotted between two without
 * renumbering. Two colours repeat because `APPOINTMENT_COLORS` is six and this
 * list is eight - blue, amber, red and green are spoken for by the severity ramp
 * and teal by the accent. The repeats sit four rows apart so they are unlikely
 * to land side by side.
 */
export const VISIT_TYPE_PRESENTATION: Record<
  VisitTypeCode,
  { color: AppointmentColor; order: number }
> = {
  fiber_installation: { color: "cat-1", order: 10 },
  fiber_repair: { color: "cat-6", order: 20 },
  fiber_splicing: { color: "cat-2", order: 30 },
  fiber_testing: { color: "cat-4", order: 40 },
  outside_plant_construction: { color: "cat-5", order: 50 },
  structured_cabling: { color: "cat-3", order: 60 },
  wireless_small_cell_das: { color: "cat-2", order: 70 },
  network_maintenance: { color: "cat-5", order: 80 },
};

/**
 * A type added from the console after this shipped has no entry here, so it gets
 * slate and sorts last rather than rendering blank. That is the cost of keeping
 * presentation off the table, and it is the right way round: an unstyled new
 * type still works, where a missing colour column would not.
 */
export function visitTypeColor(code: string): AppointmentColor {
  return isVisitTypeCode(code) ? VISIT_TYPE_PRESENTATION[code].color : "cat-5";
}

export function visitTypeOrder(code: string): number {
  return isVisitTypeCode(code) ? VISIT_TYPE_PRESENTATION[code].order : 999;
}
